import { useMutation, useQuery } from "@apollo/client";
import {
	Anchor,
	Badge,
	Button,
	Card,
	Container,
	Group,
	Loader,
	Modal,
	ScrollArea,
	Stack,
	Table,
	Text,
	Title,
	Tooltip,
} from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import { IconBuilding, IconTrash, IconUser } from "@tabler/icons-react";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useEffect } from "react";
import type { EmployeeGetOrganizationQuery } from "../../../graphql/generated/types.gen";
import {
	EMPLOYEE_DELETE_ORGANIZATION,
	EMPLOYEE_GET_ORGANIZATION,
} from "../../../graphql/queries";
import { useAuthCompat } from "../../../hooks/useAuthCompat";
import { useIsMobile } from "../../../hooks/useIsMobile";

export const Route = createFileRoute("/admin/organizations/$organizationId")({
	component: AdminOrganizationDetailPage,
});

function AdminOrganizationDetailPage() {
	const { organizationId } = Route.useParams();
	const { user: currentUser, isLoading: authLoading } = useAuthCompat();
	const isMobile = useIsMobile();
	const navigate = useNavigate();

	const [
		deleteModalOpened,
		{ open: openDeleteModal, close: closeDeleteModal },
	] = useDisclosure(false);

	useEffect(() => {
		if (authLoading) return;
		if (!currentUser) {
			window.location.href = "/welcome";
			return;
		}
		if (!currentUser.isEmployee) {
			window.location.href = "/";
		}
	}, [currentUser, authLoading]);

	const { data, loading } = useQuery<EmployeeGetOrganizationQuery>(
		EMPLOYEE_GET_ORGANIZATION,
		{
			variables: { id: organizationId },
			skip: !currentUser?.isEmployee,
		},
	);

	const [deleteOrg, { loading: deleting }] = useMutation(
		EMPLOYEE_DELETE_ORGANIZATION,
		{
			onCompleted: () => {
				navigate({ to: "/admin/organizations" });
			},
			onError: (error) => {
				alert(`Failed to delete organization: ${error.message}`);
			},
		},
	);

	const confirmDelete = () => {
		if (org) {
			deleteOrg({ variables: { organizationId: org.id } });
		}
	};

	if (authLoading || !currentUser?.isEmployee) {
		return (
			<div className="flex items-center justify-center min-h-screen">
				<Loader size="lg" />
			</div>
		);
	}

	if (loading) {
		return (
			<div className="flex items-center justify-center min-h-screen">
				<Loader size="lg" />
			</div>
		);
	}

	const org = data?.employeeOrganization;

	if (!org) {
		return (
			<div className="h-[calc(100vh-var(--header-height))] overflow-auto bg-[var(--mantine-color-body)]">
				<Container size="lg" py="xl" px={isMobile ? "md" : "xl"}>
					<div className="mb-6">
						<Anchor href="/admin/organizations">
							&larr; Back to Organizations
						</Anchor>
					</div>
					<Card shadow="sm" padding="xl" radius="md" withBorder>
						<Text size="lg" c="dimmed" ta="center">
							Organization not found.
						</Text>
					</Card>
				</Container>
			</div>
		);
	}

	return (
		<div className="h-[calc(100vh-var(--header-height))] overflow-auto bg-[var(--mantine-color-body)]">
			<Container size="lg" py="xl" px={isMobile ? "md" : "xl"}>
				<Group gap="md" mb="xs" wrap="nowrap">
					<IconBuilding
						size={isMobile ? 24 : 32}
						color="var(--mantine-color-blue-6)"
					/>
					<Title order={isMobile ? 2 : 1}>{org.name}</Title>
				</Group>
				<div className="mb-6">
					<Anchor href="/admin/organizations">
						&larr; Back to Organizations
					</Anchor>
				</div>

				<Stack gap="lg">
					<Card shadow="sm" padding="lg" radius="md" withBorder>
						<Title order={3} mb="md">
							Organization Details
						</Title>
						<Stack gap="sm">
							<Group>
								<Text fw={500} w={120}>
									Name:
								</Text>
								<Text>{org.name}</Text>
							</Group>
							<Group>
								<Text fw={500} w={120}>
									Website:
								</Text>
								<Text>{org.websiteUrl || "—"}</Text>
							</Group>
							<Group>
								<Text fw={500} w={120}>
									WorkOS ID:
								</Text>
								<Text size="sm" c="dimmed" style={{ wordBreak: "break-all" }}>
									{org.workosId}
								</Text>
							</Group>
							<Group>
								<Text fw={500} w={120}>
									Created:
								</Text>
								<Tooltip label={new Date(org.createdAt).toLocaleString()}>
									<Text style={{ cursor: "default" }}>
										{new Date(org.createdAt).toLocaleDateString()}
									</Text>
								</Tooltip>
							</Group>
							<Group>
								<Text fw={500} w={120}>
									Updated:
								</Text>
								<Tooltip label={new Date(org.updatedAt).toLocaleString()}>
									<Text style={{ cursor: "default" }}>
										{new Date(org.updatedAt).toLocaleDateString()}
									</Text>
								</Tooltip>
							</Group>
							<Group>
								<Text fw={500} w={120}>
									AI Cost:
								</Text>
								<Text>${(org.aiCostUsd ?? 0).toFixed(2)}</Text>
							</Group>
						</Stack>
					</Card>

					<Card shadow="sm" padding="lg" radius="md" withBorder>
						<Title order={3} mb="md">
							Actions
						</Title>
						<Group>
							<Button
								leftSection={<IconTrash size={16} />}
								color="red"
								variant="light"
								onClick={openDeleteModal}
							>
								Delete Organization
							</Button>
						</Group>
					</Card>

					<Card shadow="sm" padding="lg" radius="md" withBorder>
						<Title order={3} mb="md">
							Members ({org.members.length})
						</Title>
						{org.members.length === 0 ? (
							<Text c="dimmed">No members.</Text>
						) : isMobile ? (
							<Stack gap="sm">
								{org.members.map((member) => (
									<Card
										key={member.user.id}
										padding="sm"
										radius="sm"
										withBorder
									>
										<Group justify="space-between">
											<Link
												to="/admin/users/$userId"
												params={{ userId: member.user.id }}
												style={{ textDecoration: "none" }}
											>
												<Group gap="xs">
													<IconUser size={16} />
													<Text fw={500}>{member.user.displayName}</Text>
												</Group>
											</Link>
											<Badge size="sm">{member.role}</Badge>
										</Group>
										<Text size="xs" c="dimmed" mt="xs">
											{member.user.email}
										</Text>
										<Tooltip
											label={new Date(member.createdAt).toLocaleString()}
										>
											<Text size="xs" c="dimmed" style={{ cursor: "default" }}>
												Joined {new Date(member.createdAt).toLocaleDateString()}
											</Text>
										</Tooltip>
										<Tooltip
											label={
												member.lastActiveAt
													? new Date(member.lastActiveAt).toLocaleString()
													: "No activity"
											}
										>
											<Text size="xs" c="dimmed" style={{ cursor: "default" }}>
												Last active:{" "}
												{member.lastActiveAt
													? new Date(member.lastActiveAt).toLocaleDateString()
													: "Never"}
											</Text>
										</Tooltip>
									</Card>
								))}
							</Stack>
						) : (
							<ScrollArea>
								<Table striped highlightOnHover>
									<Table.Thead>
										<Table.Tr>
											<Table.Th>User</Table.Th>
											<Table.Th>Email</Table.Th>
											<Table.Th>Role</Table.Th>
											<Table.Th>Joined</Table.Th>
											<Table.Th>Last Active</Table.Th>
										</Table.Tr>
									</Table.Thead>
									<Table.Tbody>
										{org.members.map((member) => (
											<Table.Tr key={member.user.id}>
												<Table.Td>
													<Link
														to="/admin/users/$userId"
														params={{ userId: member.user.id }}
														style={{ textDecoration: "none" }}
													>
														<Group gap="xs">
															<IconUser size={16} />
															<Text size="sm">{member.user.displayName}</Text>
														</Group>
													</Link>
												</Table.Td>
												<Table.Td>{member.user.email}</Table.Td>
												<Table.Td>
													<Badge size="sm">{member.role}</Badge>
												</Table.Td>
												<Table.Td>
													<Tooltip
														label={new Date(member.createdAt).toLocaleString()}
													>
														<Text size="sm" style={{ cursor: "default" }}>
															{new Date(member.createdAt).toLocaleDateString()}
														</Text>
													</Tooltip>
												</Table.Td>
												<Table.Td>
													<Tooltip
														label={
															member.lastActiveAt
																? new Date(member.lastActiveAt).toLocaleString()
																: "No activity"
														}
													>
														<Text size="sm" style={{ cursor: "default" }}>
															{member.lastActiveAt
																? new Date(
																		member.lastActiveAt,
																	).toLocaleDateString()
																: "Never"}
														</Text>
													</Tooltip>
												</Table.Td>
											</Table.Tr>
										))}
									</Table.Tbody>
								</Table>
							</ScrollArea>
						)}
					</Card>
				</Stack>
			</Container>

			<Modal
				opened={deleteModalOpened}
				onClose={closeDeleteModal}
				title="Delete Organization"
				centered
			>
				<Stack>
					<Text>
						Are you sure you want to delete <strong>{org.name}</strong>?
					</Text>
					<Text size="sm" c="dimmed">
						This will remove the organization and all associated data including
						nodes, edges, and member associations. This action cannot be undone.
					</Text>
					<Group justify="flex-end" mt="md">
						<Button variant="default" onClick={closeDeleteModal}>
							Cancel
						</Button>
						<Button color="red" onClick={confirmDelete} loading={deleting}>
							Delete
						</Button>
					</Group>
				</Stack>
			</Modal>
		</div>
	);
}
